"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight, CheckCircle2 } from "@/components/icons";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import SectionHeader from "@/components/ui/section-header";
import { FREE_TRIAL_LABEL, FREE_TRIAL_URL, STATS } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { IPComparisonPanel, BandwidthRolloverVisual } from "./parts";
import dynamic from "next/dynamic";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";

const GlobeComponent = dynamic(() => import("@/components/sections/shared/globe"), {
  ssr: false,
  loading: () => (
    <div className="bg-foreground/[0.03] aspect-square w-full animate-pulse rounded-full" />
  ),
});

export default function ResidentialProductHighlights() {
  const prefersReducedMotion = useReducedMotion();
  const maintenanceMode = useMaintenanceMode();
  const trialHref = maintenanceMode ? MAINTENANCE_PATH : FREE_TRIAL_URL;

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: inViewScrollRootMargin },
          transition: { duration: 0.5, delay },
        };

  return (
    <>
      <section
        id="real-ip-section"
        className="full-bleed band-wash py-section border-border overflow-hidden border-b"
        aria-labelledby="real-ip-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)}>
              <SectionHeader
                eyebrow="Infrastructure"
                id="real-ip-heading"
                heading="Built on Real Residential IPs"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Proxidize&apos;s residential proxy network spans {STATS.countries} countries with a
                deep IP pool, simple per-GB pricing, and no artificial limitations. Every connection
                routes through real residential infrastructure, sourced and maintained for long-term
                reliability.
              </p>

              <div className="mb-8 grid grid-cols-2 gap-3">
                {[
                  { val: STATS.ips, label: "Residential IPs", sub: "Ethically sourced" },
                  { val: STATS.countries, label: "Countries", sub: "Full geographic coverage" },
                  { val: STATS.uptime, label: "Platform Uptime", sub: "Dashboard & API" },
                  { val: "24/7", label: "Support", sub: "Live chat and email" },
                ].map((item) => (
                  <div key={item.label} className="border-border bg-card rounded-xl border p-3">
                    <p className="text-muted-foreground mb-0.5 text-[10px] font-bold tracking-wider uppercase">
                      {item.val}
                    </p>
                    <p className="text-foreground text-sm font-bold">{item.label}</p>
                    <p className="text-muted-foreground mt-0.5 text-[11px]">{item.sub}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeUp(0.12)}>
              <IPComparisonPanel />
            </motion.div>
          </div>
        </div>
      </section>

      <section
        id="worldwide-coverage-section"
        /* bg-band is the homepage's alternating section tint (#0e0e10 dark,
           #f6f9fc light) — a step above the page and below the cards, so tiles
           inside still read as elevated on it.

           full-bleed: the shell constrains sections to max-w-7xl, so without it
           the band would stop at the two frame hairlines instead of running the
           full viewport width. The inner max-w-7xl wrapper keeps the content
           aligned with every other section. */
        className="full-bleed band-wash py-section-sm border-border bg-band relative isolate overflow-hidden border-b"
        aria-labelledby="worldwide-coverage-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)} className="relative order-2 min-w-0 lg:order-1">
              <GlobeComponent ariaLabel="Interactive 3D globe showing worldwide residential proxy coverage" />
            </motion.div>

            <motion.div {...fadeUp(0.12)} className="order-1 lg:order-2">
              <SectionHeader
                eyebrow="Coverage"
                id="worldwide-coverage-heading"
                heading="Residential proxy locations and targeting"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Target any of our{" "}
                <span className="text-foreground font-semibold">{STATS.countries} countries</span>.
                down to the city or ISP level. Go beyond country and route every request through the
                exact location your use case requires.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        id="bandwidth-section"
        className="full-bleed band-wash py-section border-border overflow-hidden border-b"
        aria-labelledby="bandwidth-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)}>
              <SectionHeader
                eyebrow="Flexibility"
                id="bandwidth-heading"
                heading={<>Your Bandwidth is Yours</>}
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Buy the bandwidth you need. Anything unused rolls over to the next month, and if you
                cancel, your remaining balance stays in your account{" "}
                <span className="text-foreground font-semibold">your bandwidth never expires.</span>
              </p>

              <ul className="mb-8 space-y-2.5">
                {[
                  "Unused GB rolls over every month",
                  "Bandwidth stays available after cancellation",
                  "No forced expiry on purchased data",
                  "Simple per-GB pricing, no hidden fees",
                  "Scale up or down at any time",
                ].map((feat) => (
                  <li key={feat} className="text-muted-foreground flex items-center gap-3 text-sm">
                    <CheckCircle2
                      size={14}
                      className="text-brand-green shrink-0"
                      aria-hidden="true"
                    />
                    {feat}
                  </li>
                ))}
              </ul>

              <Link
                href={trialHref}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ size: "default" }),
                  "bg-primary text-primary-foreground border-transparent font-semibold"
                )}
              >
                {FREE_TRIAL_LABEL}
                <ArrowRight size={14} className="ml-1.5" aria-hidden="true" />
              </Link>
            </motion.div>

            <motion.div {...fadeUp(0.12)}>
              <BandwidthRolloverVisual />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
